import React from 'react';
import { useSelector } from 'react-redux';
import { Col, Row } from 'antd';
import moment from 'moment';

import { ReactComponent as ArrowDownIcon } from 'app/assets/img/arrow-down-icon.svg';
import { ReactComponent as CalendarIcon } from 'app/assets/img/calendar-icon.svg';

import { selectTranslations } from 'app/pages/App/slice/selectors';
import StyledButton from 'app/components/StyledButton';
import StyledSelect from '../components/StyledSelect';
import StyledDatePicker from '../components/StyledDatePicker';
import { formatDate } from '../CheckTickets/HeaderFilter';
import StyledInputNumber from '../components/StyledInputNumber';
import { APPROVAL_ORDER_STATUS_OPTIONS, ApprovalOrderFilter } from '.';
import useWindowSize from 'app/hooks/useWindowSize';
import { convertVItoEN } from '../CheckTickets/helper';

export interface SearchProps {
  approvalOrderFilter: ApprovalOrderFilter;
  onChangeFilter: (filter: Partial<ApprovalOrderFilter>) => void;
  onSubmitFilter: () => void;
  agentData: { label: string; value: number }[];
}

const ApprovalOrderSearch: React.FC<SearchProps> = props => {
  const { approvalOrderFilter, agentData, onChangeFilter, onSubmitFilter } =
    props;

  const translations = useSelector(selectTranslations);
  const { device } = useWindowSize();
  const isDesktop = device === 'desktop';

  return (
    <Row gutter={[12, 12]}>
      <Col style={{ width: isDesktop ? 220 : '100%' }}>
        <StyledDatePicker
          placeholder={translations?.['b2b.txt.date-order'] ?? ''}
          format={date => formatDate(date, translations)}
          onChange={event => {
            const momentValue = moment(event);
            onChangeFilter({
              orderDate: momentValue.isValid()
                ? momentValue.format('YYYY-MM-DD')
                : null,
            });
          }}
          suffixIcon={<CalendarIcon />}
          value={
            moment(approvalOrderFilter.orderDate).isValid()
              ? moment(approvalOrderFilter.orderDate)
              : null
          }
          allowClear
          minWidthInput="initial"
          style={{ width: isDesktop ? 'auto' : '100%' }}
        />
      </Col>
      <Col style={{ width: isDesktop ? 'calc(100% - 368px)' : '100%' }}>
        <Row gutter={[12, 12]}>
          <Col span={isDesktop ? 8 : 24}>
            <StyledInputNumber
              allowLeadingZeros
              placeholder={translations?.['b2c.txt.orderNumber'] ?? ''}
              width="100%"
              onChange={event => {
                onChangeFilter({ orderId: event.target.value });
              }}
              value={approvalOrderFilter.orderId}
            />
          </Col>
          <Col span={isDesktop ? 8 : 24}>
            <StyledSelect
              filterOption={(input, option) =>
                (convertVItoEN(option?.label) ?? '').includes(
                  convertVItoEN(input),
                )
              }
              showSearch
              isFocus
              suffixIcon={<ArrowDownIcon />}
              placeholder={translations?.['b2b.txt.orderer'] ?? ''}
              options={[
                { label: translations?.['b2b.text.all'] ?? '', value: '' },
                ...agentData,
              ]}
              style={{ width: '100%' }}
              allowClear
              onChange={(value: any) => {
                onChangeFilter({ agentId: value });
              }}
              value={approvalOrderFilter.agentId}
            />
          </Col>
          <Col span={isDesktop ? 8 : 24}>
            <StyledSelect
              filterOption={(input, option) =>
                (convertVItoEN(option?.label) ?? '').includes(
                  convertVItoEN(input),
                )
              }
              showSearch
              isFocus
              suffixIcon={<ArrowDownIcon />}
              placeholder={translations?.['b2b.txt.status'] ?? ''}
              options={[
                { label: translations?.['b2b.text.all'] ?? '', value: '' },
                ...APPROVAL_ORDER_STATUS_OPTIONS.map(item => ({
                  label: translations?.[item.label] ?? '',
                  value: item.value,
                })),
              ]}
              style={{ width: '100%' }}
              allowClear
              onChange={(value: any) => {
                onChangeFilter({ subOrderState: value });
              }}
              value={approvalOrderFilter.subOrderState}
            />
          </Col>
        </Row>
      </Col>
      <Col style={{ width: isDesktop ? 'initial' : '100%' }}>
        <StyledButton
          style={{
            background: '#FFFFFF',
            border: '1px solid #F8B117',
            color: '#F8B117',
            width: isDesktop ? 124 : '100%',
            height: 44,
            flex: 1,
          }}
          onClick={() => onSubmitFilter()}
        >
          {translations ? translations['b2c.txt.search'] : ''}
        </StyledButton>
      </Col>
    </Row>
  );
};

export default ApprovalOrderSearch;
